import { useEffect } from "react";
import { ArrowDown, ArrowUpRight, Check, Radar, Sparkles, Waypoints } from "lucide-react";
import { brand } from "./brand";
import { WaitlistForm } from "./components/WaitlistForm";
import { analytics } from "./lib/analytics";

const agents = [
  { code: "01", title: "Understand", copy: "Reads your site, deck, calls, and founder context. Builds an evidence-backed market map." },
  { code: "02", title: "Find", copy: "Refines the ICP, researches accounts, detects intent, and ranks the next best opportunities." },
  { code: "03", title: "Create", copy: "Ships organic pages, paid landing experiences, and account-specific buying rooms." },
  { code: "04", title: "Engage", copy: "Starts human-approved outreach, handles replies, and helps book qualified conversations." },
  { code: "05", title: "Learn", copy: "Turns every response and call into sharper positioning, ICP, campaigns, and roadmap signals." },
];

function App() {
  useEffect(() => analytics.track({ name: "page_view", path: window.location.pathname }), []);
  return (
    <div className="site-shell">
      <header className="nav wrap">
        <a className="wordmark" href="#top" aria-label={`${brand.name} home`}><span>DR</span>{brand.name}</a>
        <div className="nav-note"><i /> Building in public · {brand.launchWindow}</div>
        <a className="nav-cta" href="#join">Join waitlist <ArrowUpRight size={15} /></a>
      </header>

      <main id="top">
        <section className="hero wrap">
          <div className="hero-copy">
            <p className="eyebrow"><Sparkles size={14} /> {brand.eyebrow}</p>
            <h1>Turn market signal<br /><strong>into compounding<br />demand.</strong></h1>
            <p className="lede">{brand.subhead}</p>
            <a className="text-link" href="#system">See the engine <ArrowDown size={17} /></a>
          </div>
          <div className="hero-card" id="join">
            <div className="card-topline"><span>Private access</span><span>Limited founding cohort</span></div>
            <h2>Join the founding cohort.</h2>
            <p>Built for B2B SaaS founders with something valuable to sell—and no time to operate five disconnected growth tools.</p>
            <WaitlistForm />
          </div>
        </section>

        <div className="ticker" aria-hidden="true"><div>RESEARCH → POSITION → BUILD → ENGAGE → LISTEN → LEARN →&nbsp; RESEARCH → POSITION → BUILD → ENGAGE → LISTEN → LEARN →&nbsp;</div></div>

        <section className="problem wrap" id="system">
          <div className="section-kicker"><span>01</span> The missing company</div>
          <div className="problem-grid">
            <h2>One founder.<br />Five jobs.<br /><em>Zero feedback loops.</em></h2>
            <div>
              <p>Today’s growth stack gives you more tools to operate. {brand.name} connects the work—and preserves the evidence behind every decision.</p>
              <ul>
                <li><Check size={17} /> One context layer, not five disconnected briefs</li>
                <li><Check size={17} /> Evidence and approvals before external action</li>
                <li><Check size={17} /> Every market signal flows back into strategy</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="engine">
          <div className="wrap">
            <div className="section-kicker light"><span>02</span> The compounding loop</div>
            <div className="engine-head"><h2>Not another copilot.<br /><em>A distribution crew.</em></h2><Radar size={80} strokeWidth={1} /></div>
            <div className="agent-grid">
              {agents.map((agent) => <article key={agent.code}><span>{agent.code}</span><h3>{agent.title}</h3><p>{agent.copy}</p></article>)}
            </div>
            <div className="loop-line"><Waypoints size={22} /><span>One shared memory</span><i /><span>Human approval gates</span><i /><span>Learning with every signal</span></div>
          </div>
        </section>

        <section className="proof wrap">
          <div className="section-kicker"><span>03</span> What compounds</div>
          <h2>Give it one URL.<br /><em>Get a living go-to-market system.</em></h2>
          <div className="proof-grid">
            <div><b>01 / CONTEXT</b><h3>Company understood</h3><p>Product, proof, market, language, and gaps extracted with citations.</p></div>
            <div><b>02 / FOCUS</b><h3>ICP challenged</h3><p>Hypotheses built, tested against real accounts, and refined with evidence.</p></div>
            <div><b>03 / ACTION</b><h3>Campaign ready</h3><p>Buying rooms and outreach drafted for review—not blindly sprayed.</p></div>
            <div><b>04 / SIGNAL</b><h3>System learning</h3><p>Replies, objections, and call feedback sharpen every next move.</p></div>
          </div>
        </section>

        <section className="quote"><div className="wrap"><span>“</span><blockquote>{brand.founderQuote}</blockquote></div></section>

        <section className="final-cta wrap">
          <p>{brand.promise}</p>
          <h2>Build product.<br /><em>We’ll build momentum.</em></h2>
          <a href="#join">Join the founding cohort <ArrowUpRight /></a>
        </section>
      </main>

      <footer className="wrap"><div className="wordmark"><span>DR</span>{brand.name}</div><p>© 2026 · Built for remarkable things.</p><a href="#join">{brand.contactLabel}</a></footer>
    </div>
  );
}

export default App;
